import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Clock,
  Play,
  Pause,
  Trash2,
  Plus,
  RefreshCw,
  Calendar,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Activity,
  Timer,
  Bell,
  BellOff,
  History,
  Zap
} from 'lucide-react';

const MonitorScheduler = () => {
  const [schedules, setSchedules] = useState([]);
  const [monitors, setMonitors] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [recentExecutions, setRecentExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  const [form, setForm] = useState({
    monitor_id: '',
    monitor_name: '',
    frequency: 'daily',
    custom_cron: '',
    description: '',
    notify_on_complete: true,
    notify_on_error: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [schedulesRes, monitorsRes, freqRes, execRes] = await Promise.all([
        api.get('/schedules'),
        api.get('/monitors'),
        api.get('/schedules/frequencies'),
        api.get('/schedules/executions/recent?limit=10')
      ]);
      
      setSchedules(schedulesRes.data || []);
      setMonitors(monitorsRes.data?.items || monitorsRes.data || []);
      setFrequencies(freqRes.data?.frequencies || []);
      setRecentExecutions(execRes.data || []);
    } catch (error) {
      toast.error('Error al cargar datos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.monitor_id) {
      toast.error('Selecciona un monitor');
      return;
    }
    
    try {
      const res = await api.post('/schedules', form);
      setSchedules(prev => [res.data, ...prev]);
      setIsDialogOpen(false);
      resetForm();
      toast.success('Programación creada');
    } catch (error) {
      toast.error('Error al crear programación');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta programación?')) return;
    
    try {
      await api.delete(`/schedules/${id}`);
      setSchedules(prev => prev.filter(s => s.id !== id));
      toast.success('Programación eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleToggle = async (schedule) => {
    try {
      const res = await api.post(`/schedules/${schedule.id}/toggle`);
      setSchedules(prev => prev.map(s => s.id === schedule.id ? res.data : s));
      toast.success(res.data.active ? 'Programación activada' : 'Programación pausada');
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleRunNow = async (schedule) => {
    try {
      toast.info(`Ejecutando ${schedule.monitor_name}...`);
      const res = await api.post(`/schedules/${schedule.id}/run-now`);
      
      if (res.data.success) {
        toast.success(`Monitor ejecutado. Alertas generadas: ${res.data.alerts_generated || 0}`);
      } else {
        toast.error(res.data.error || 'Error en la ejecución');
      }
      
      // Recargar datos
      loadData();
    } catch (error) {
      toast.error('Error al ejecutar');
    }
  };

  const resetForm = () => {
    setForm({
      monitor_id: '',
      monitor_name: '',
      frequency: 'daily',
      custom_cron: '',
      description: '',
      notify_on_complete: true,
      notify_on_error: true
    });
  };

  const handleMonitorSelect = (monitorId) => {
    const monitor = monitors.find(m => m.id === monitorId);
    setForm(prev => ({
      ...prev,
      monitor_id: monitorId,
      monitor_name: monitor?.nombre || ''
    }));
  };

  const getFrequencyLabel = (freq) => {
    return frequencies.find(f => f.value === freq)?.label || freq;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Monitores sin programación
  const unscheduledMonitors = monitors.filter(
    m => !schedules.some(s => s.monitor_id === m.id)
  );

  return (
    <div className="space-y-6 p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Calendar className="text-indigo-600" size={24} />
            Programación de Monitores
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Configura la ejecución automática de monitores
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button style={{ backgroundColor: '#4f46e5' }}>
                <Plus size={16} className="mr-2" />
                Nueva Programación
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Nueva Programación</DialogTitle>
                <DialogDescription>
                  Configura la ejecución automática de un monitor
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <Label>Monitor</Label>
                  <Select
                    value={form.monitor_id}
                    onValueChange={handleMonitorSelect}
                  >
                    <SelectTrigger className="mt-1" style={{ backgroundColor: '#fff' }}>
                      <SelectValue placeholder="Seleccionar monitor..." />
                    </SelectTrigger>
                    <SelectContent>
                      {unscheduledMonitors.length === 0 ? (
                        <div className="p-2 text-sm text-slate-500">
                          Todos los monitores tienen programación
                        </div>
                      ) : (
                        unscheduledMonitors.map(m => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.nombre}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Frecuencia</Label>
                  <Select
                    value={form.frequency}
                    onValueChange={(v) => setForm(prev => ({ ...prev, frequency: v }))}
                  >
                    <SelectTrigger className="mt-1" style={{ backgroundColor: '#fff' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {frequencies.map(f => (
                        <SelectItem key={f.value} value={f.value}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {form.frequency === 'custom_cron' && (
                  <div>
                    <Label>Expresión Cron</Label>
                    <Input
                      value={form.custom_cron}
                      onChange={(e) => setForm(prev => ({ ...prev, custom_cron: e.target.value }))}
                      placeholder="0 8 * * * (min hora día mes díaSemana)"
                      className="mt-1 font-mono"
                      style={{ backgroundColor: '#fff' }}
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Formato: minuto hora día_mes mes día_semana
                    </p>
                  </div>
                )}
                
                <div>
                  <Label>Descripción (opcional)</Label>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Ej: Monitoreo diario de transacciones"
                    className="mt-1"
                    style={{ backgroundColor: '#fff' }}
                  />
                </div>
                
                <div className="space-y-3 pt-2">
                  <Label className="text-sm font-medium">Notificaciones</Label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm text-slate-600 flex items-center gap-2">
                      <Bell size={14} className="text-green-600" />
                      Notificar al completar
                    </span>
                    <Switch
                      checked={form.notify_on_complete}
                      onCheckedChange={(v) => setForm(prev => ({ ...prev, notify_on_complete: v }))}
                    />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm text-slate-600 flex items-center gap-2">
                      <AlertTriangle size={14} className="text-red-600" />
                      Notificar en error
                    </span>
                    <Switch
                      checked={form.notify_on_error}
                      onCheckedChange={(v) => setForm(prev => ({ ...prev, notify_on_error: v }))}
                    />
                  </label>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreate}
                  style={{ backgroundColor: '#4f46e5' }}
                  disabled={!form.monitor_id}
                >
                  Crear
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Lista de Programaciones */}
        <div className="xl:col-span-2 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin text-slate-400" size={24} />
            </div>
          ) : schedules.length === 0 ? (
            <Card className="bg-white">
              <CardContent className="py-12 text-center">
                <Calendar size={48} className="mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No hay programaciones configuradas</p>
                <p className="text-sm text-slate-400 mt-1">
                  Crea una programación para automatizar la ejecución de monitores
                </p>
              </CardContent>
            </Card>
          ) : (
            schedules.map(schedule => (
              <Card
                key={schedule.id}
                className={`bg-white transition-all ${!schedule.active ? 'opacity-60' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-slate-900">
                          {schedule.monitor_name}
                        </h4>
                        <Badge
                          variant="outline"
                          className={schedule.active 
                            ? 'bg-green-50 text-green-700 border-green-200' 
                            : 'bg-slate-50 text-slate-500'}
                        >
                          {schedule.active ? 'Activo' : 'Pausado'}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {getFrequencyLabel(schedule.frequency)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Activity size={14} />
                          {schedule.run_count} ejecuciones
                        </span>
                        {schedule.error_count > 0 && (
                          <span className="flex items-center gap-1 text-red-500">
                            <AlertTriangle size={14} />
                            {schedule.error_count} errores
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span>
                          Última: {formatDate(schedule.last_run)}
                          {schedule.last_status && (
                            schedule.last_status === 'success' 
                              ? <CheckCircle size={12} className="inline ml-1 text-green-500" />
                              : <XCircle size={12} className="inline ml-1 text-red-500" />
                          )}
                        </span>
                        <span>
                          Próxima: {formatDate(schedule.next_run)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRunNow(schedule)}
                        title="Ejecutar ahora"
                        className="text-indigo-600"
                      >
                        <Zap size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggle(schedule)}
                        title={schedule.active ? 'Pausar' : 'Activar'}
                      >
                        {schedule.active ? <Pause size={16} /> : <Play size={16} />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(schedule.id)}
                        className="text-red-500 hover:text-red-600"
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Notificaciones */}
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-100">
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      {schedule.notify_on_complete 
                        ? <Bell size={12} className="text-green-500" />
                        : <BellOff size={12} className="text-slate-300" />}
                      Completado
                    </span>
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      {schedule.notify_on_error 
                        ? <Bell size={12} className="text-red-500" />
                        : <BellOff size={12} className="text-slate-300" />}
                      Error
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Panel lateral - Ejecuciones recientes */}
        <div className="space-y-4">
          <Card className="bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <History size={18} className="text-slate-600" />
                Ejecuciones Recientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentExecutions.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">
                  No hay ejecuciones recientes
                </p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {recentExecutions.map((exec, idx) => (
                    <div key={idx} className="p-2 bg-slate-50 rounded text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-700 truncate">
                          {exec.monitor_name}
                        </span>
                        {exec.status === 'success' ? (
                          <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                        ) : (
                          <XCircle size={14} className="text-red-500 flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-1 text-slate-400">
                        <span>{formatDate(exec.executed_at)}</span>
                        <span className="flex items-center gap-1">
                          <Timer size={10} />
                          {exec.duration_ms}ms
                        </span>
                      </div>
                      {exec.alerts_generated > 0 && (
                        <div className="mt-1 text-amber-600">
                          {exec.alerts_generated} alertas generadas
                        </div>
                      )}
                      {exec.error && (
                        <div className="mt-1 text-red-500 truncate" title={exec.error}>
                          {exec.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Panel */}
          <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 border-indigo-200">
            <CardContent className="pt-4">
              <h4 className="font-medium text-indigo-900 mb-2">
                ¿Cómo funciona?
              </h4>
              <ul className="space-y-2 text-sm text-indigo-800">
                <li className="flex items-start gap-2">
                  <Clock size={14} className="mt-0.5 text-indigo-600" />
                  <span>El sistema ejecuta los monitores automáticamente según la frecuencia configurada</span>
                </li>
                <li className="flex items-start gap-2">
                  <Bell size={14} className="mt-0.5 text-indigo-600" />
                  <span>Recibe notificaciones cuando se complete o falle una ejecución</span>
                </li>
                <li className="flex items-start gap-2">
                  <Zap size={14} className="mt-0.5 text-indigo-600" />
                  <span>Usa "Ejecutar ahora" para probar un monitor sin esperar</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MonitorScheduler;
